import dynamic from "next/dynamic";
import React, { useEffect, useRef, useState } from "react";

import { useDashboardContext } from "../..";
import { getAdjustedHeight } from "../../utils/getDashboardAdjustedHeight";
import { SaveUnsaveJobHandlerType } from "../types";

const JobDetail = dynamic<any>(
  () => import("@monsterindia/seeker-jobs").then((mod) => mod.JobDetail),
  {
    ssr: false,
  },
);

interface JobStatusCardProps {
  selectedData: any;
  jobDescData: {
    jobDetails: any;
    filterStatusPresent: boolean;
  };
  siteProps: any;
  sitePropsProperties: sitePropsType | {};
  loader: boolean;
  mediaUrl: string;
  leftSideRef: React.RefObject<HTMLDivElement> | undefined;
  savedJobs: number[];
  saveUnsaveJobHandler?: SaveUnsaveJobHandlerType;
}

const JobStatusCard: React.FC<JobStatusCardProps> = ({
  selectedData,
  jobDescData,
  siteProps,
  sitePropsProperties,
  loader,
  mediaUrl,
  leftSideRef,
  savedJobs,
  saveUnsaveJobHandler,
}) => {
  const containerRef = useRef<any>(null);
  const { isMobile } = useDashboardContext();
  const [isAtBottom, setIsAtBottom] = useState(false);

  const adjustHeight = () => {
    if (containerRef.current) {
      if (isMobile) {
        const totalChildHeight = Array.from(
          containerRef.current.children,
        ).reduce(
          (sum: number, child) => sum + (child as HTMLElement).clientHeight,
          0,
        );

        const viewportHeight = window.innerHeight;
        if (totalChildHeight < viewportHeight) {
          containerRef.current.style.height = `${viewportHeight - 60}px`;
        } else {
          containerRef.current.style.height = `100%`;
        }
      } else {
        const remainingHeight = getAdjustedHeight("saved");
        containerRef.current.style.height = `${remainingHeight}px`;
      }
    }
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setIsAtBottom(true);
      } else {
        setIsAtBottom(false);
      }
    }
  };

  useEffect(() => {
    const section = containerRef.current;
    if (!isMobile && section) {
      section.addEventListener("scroll", handleScroll);

      return () => {
        section.removeEventListener("scroll", handleScroll);
      };
    }
    return () => {};
  }, []);

  useEffect(() => {
    adjustHeight();
    const resizeObserver = new ResizeObserver(adjustHeight);
    if (containerRef.current) {
      Array.from(containerRef.current.children).forEach((child) => {
        resizeObserver.observe(child as HTMLElement);
      });
    }

    window.addEventListener("resize", adjustHeight);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", adjustHeight);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`md:!border-border-subtle scrollbar-hide md:!rounded-2xl md:!rounded-b-none md:!border-l md:!border-r md:!border-t md:!border-solid ${!isMobile ? "overflow-y-scroll" : ""} ${isAtBottom ? "md:!rounded-bl-2xl md:!rounded-br-2xl md:!border-b" : ""}`}
    >
      <JobDetail
        key={
          selectedData.id || selectedData.jobId || selectedData.kiwiJobId || ""
        }
        jobData={jobDescData.jobDetails}
        isLoggedIn={true}
        isMobile={isMobile}
        host={window.location.host}
        pageName={"Jobs"}
        isSyndicateQuickApplyEnabled={false}
        isJobSaved={savedJobs.includes(selectedData.jobId)}
        showJDLoader={loader}
        savedJobCallback={({ type = "", value = false, id = "" }) => {
          if (saveUnsaveJobHandler)
            saveUnsaveJobHandler(
              typeof id === "string" ? parseInt(id) : id || undefined,
              "callBack",
              {
                callBackSuccess: value,
              },
            );
        }}
        ctEventPageName={""}
      />
    </div>
  );
};

export default React.memo(JobStatusCard);
